/**
 * C.11 — NIPE chat panel.
 *
 * Expandable surface that drives the chat conversation. Sits to the
 * upper-left of the sprite when open. Test ids drive the Playwright
 * spec in C.12.
 */

import { useEffect, useMemo, useRef, useState, type ReactElement } from 'react';
import DOMPurify from 'dompurify';
import { testIds } from '../../lib/testIds';
import {
  useNipeChat,
  type NipeChatMessage,
  type UseNipeChatOptions,
} from './useNipeChat';
import { NipeTopicTileGrid } from './NipeTopicTileGrid';
import { NipeIntroBalloon } from './NipeIntroBalloon';
import { renderMarkdownToHtml } from '../../lib/sharepoint/markdownRender';
import type { NipeTopic } from './topics/index';
import { useWindowContext } from '../desktop/WindowContext';
import './NipeChatPanel.css';

export interface NipeChatPanelProps extends UseNipeChatOptions {
  isOperator: boolean;
  onClose: () => void;
}

export function NipeChatPanel({ onClose, isOperator, ...opts }: NipeChatPanelProps): ReactElement {
  const chat = useNipeChat(opts);
  const [draft, setDraft] = useState('');
  const [userHasTyped, setUserHasTyped] = useState(false);
  const inputRef = useRef<HTMLTextAreaElement | null>(null);
  const bodyRef = useRef<HTMLDivElement | null>(null);
  // J.3 — when the user clicks the "I want my own workspace" tile in the
  // NIPE topic grid, route the click through the WindowContext directly
  // (in addition to the draft-prefill the default handler does). The
  // NIPE menu is one of the 4 entry points for the WorkspaceSetupPanel.
  const { openWindow } = useWindowContext();

  // Autofocus the textarea on mount; refocus after any send / close /
  // open round-trip so the user can keep typing. Use rAF + a follow-up
  // timeout because the sprite click that opened the panel is still
  // resolving when we mount, and can steal focus.
  useEffect(() => {
    let cancelled = false;
    const focus = (): void => {
      if (cancelled) return;
      const el = inputRef.current;
      if (el) el.focus();
    };
    requestAnimationFrame(focus);
    const t1 = window.setTimeout(focus, 100);
    const t2 = window.setTimeout(focus, 350);
    return () => {
      cancelled = true;
      window.clearTimeout(t1);
      window.clearTimeout(t2);
    };
  }, []);

  // Auto-scroll body when new messages land.
  useEffect(() => {
    const el = bodyRef.current;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
  }, [chat.messages, chat.streamingText]);

  const handleSend = (): void => {
    const txt = draft.trim();
    if (!txt || chat.isRunning) return;
    setDraft('');
    void chat.send(txt);
  };

  const showIntro = chat.messages.length === 0 && !chat.streamingText;

  return (
    <>
      {showIntro ? <NipeIntroBalloon userHasTyped={userHasTyped} /> : null}
    <div className="nipe-chat-panel" data-testid={testIds.nipe.chatPanel}>
      <div className="nipe-chat-panel-header">
        <span>NIPE</span>
        <button
          type="button"
          className="nipe-chat-panel-close"
          onClick={onClose}
          aria-label="Close NIPE chat"
        >
          ×
        </button>
      </div>
      <div className="nipe-chat-panel-body" ref={bodyRef}>
        {chat.messages.length === 0 && !chat.streamingText ? (
          <NipeTopicTileGrid
            isOperator={isOperator}
            onSelectTopic={(topic: NipeTopic) => {
              // Action tiles fire a local openWindow — no LLM round-trip.
              // Pairs with the existing openSpecializationLab /
              // openOnboardWizard tools the model can also use; the
              // local path is a UX shortcut.
              if (topic.action === 'open-window' && topic.actionWindowPath) {
                openWindow(topic.actionWindowPath);
                onClose();
                return;
              }
              // J.3 — the create-private-workspace tile is one of the 4
              // entry points for the WorkspaceSetupPanel.
              if (topic.key === 'create-private-workspace') {
                openWindow('/admin/workspace-setup');
                onClose();
                return;
              }
              // Send the topic's initialPrompt (or the body as fallback)
              // straight to the model — clicking a tile previously sent
              // only "Tell me more about <label>.", which gave the model
              // nothing to work with.
              const userMessage = topic.initialPrompt ?? topic.body;
              void chat.send(userMessage);
              setUserHasTyped(true);
              inputRef.current?.focus();
            }}
          />
        ) : null}
        {chat.messages.map((m) => (
          <MessageRow key={m.id} message={m} />
        ))}
        {chat.streamingText ? (
          <div className="nipe-chat-streaming" data-testid={testIds.nipe.chatStreaming}>
            {chat.streamingText}
          </div>
        ) : null}
      </div>
      {chat.error ? <div className="nipe-chat-panel-error">{chat.error}</div> : null}
      <div className="nipe-chat-panel-input-row">
        <textarea
          ref={inputRef}
          value={draft}
          placeholder="Ask NIPE anything. Shift+Enter for a new line."
          data-testid={testIds.nipe.chatInput}
          rows={3}
          autoFocus
          onChange={(e) => {
            setDraft(e.target.value);
            if (!userHasTyped && e.target.value.length > 0) setUserHasTyped(true);
          }}
          onKeyDown={(e) => {
            if (!userHasTyped) setUserHasTyped(true);
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              handleSend();
            }
          }}
          disabled={chat.isRunning}
        />
        <div className="nipe-chat-panel-input-row-buttons">
          <span className="nipe-chat-panel-input-row-hint">
            Enter to send. Shift+Enter for a new line.
          </span>
          {chat.isRunning ? (
            <button
              type="button"
              data-testid={testIds.nipe.chatStop}
              onClick={chat.cancel}
            >
              Stop
            </button>
          ) : (
            <button
              type="button"
              data-testid={testIds.nipe.chatSend}
              onClick={handleSend}
              disabled={draft.trim().length === 0}
            >
              Send
            </button>
          )}
        </div>
      </div>
    </div>
    </>
  );
}

function MessageRow({ message }: { message: NipeChatMessage }): ReactElement {
  // Render assistant + user content as sanitized markdown HTML. Tool
  // calls + tool results render as a styled <details> block so the raw
  // JSON is inspectable but doesn't dominate the conversation.
  const sanitizedHtml = useMemo(() => {
    const raw = (message.content ?? '').trim();
    if (raw.length === 0) return '';
    // user messages are usually plain text but may contain markdown
    // (e.g. tile-seeded prompts). assistant messages routinely return
    // markdown. Treat both identically.
    const html = renderMarkdownToHtml(raw);
    return DOMPurify.sanitize(html);
  }, [message.content]);

  const prettyToolJson = useMemo(() => {
    if (!message.toolCallsJson) return null;
    try {
      const parsed = JSON.parse(message.toolCallsJson);
      return JSON.stringify(parsed, null, 2);
    } catch {
      return message.toolCallsJson;
    }
  }, [message.toolCallsJson]);

  // Role 'tool' carries raw tool-result JSON in message.content; pretty-
  // print it the same way as toolCallsJson so it doesn't leak as a wall
  // of stringified text into the conversation.
  const isToolResult = message.role === 'tool';
  const toolResultPretty = useMemo(() => {
    if (!isToolResult) return null;
    const raw = (message.content ?? '').trim();
    if (raw.length === 0) return null;
    try {
      const parsed = JSON.parse(raw);
      return JSON.stringify(parsed, null, 2);
    } catch {
      return raw;
    }
  }, [isToolResult, message.content]);

  return (
    <div
      className={`nipe-chat-message ${message.role}`}
      data-testid={testIds.nipe.chatMessage(message.id)}
    >
      {isToolResult && toolResultPretty !== null ? (
        <details className="nipe-chat-tool-block">
          <summary>tool result</summary>
          <pre>{toolResultPretty}</pre>
        </details>
      ) : sanitizedHtml.length > 0 ? (
        <div
          className="nipe-chat-message-markdown"
          // marked + DOMPurify pipeline; raw is never injected.
          dangerouslySetInnerHTML={{ __html: sanitizedHtml }}
        />
      ) : null}
      {prettyToolJson !== null && (
        <details className="nipe-chat-tool-block">
          <summary>tool call</summary>
          <pre>{prettyToolJson}</pre>
        </details>
      )}
    </div>
  );
}
